import React, { useState } from "react";
import { Phone, Mail, MapPin, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function ContactPanel() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_BASE =
    process.env.REACT_APP_API_URL || "http://localhost:5000";

  const onChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (submitted) setSubmitted(false);
    if (error) setError("");
  };

  const validateForm = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.email.trim()) return "Please enter your email address.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return "Please enter a valid email address.";
    }
    if (!form.message.trim()) return "Please enter your requirement.";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setSubmitted(false);
      return;
    }

    setLoading(true);
    setError("");
    setSubmitted(false);

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          message: form.message.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to send enquiry.");
      }

      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        company: "",
        message: "",
      });
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.message || "Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-[2rem] bg-cyan-800 p-8 text-white shadow-xl shadow-cyan-900/20 md:p-10">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-100">
          Contact Us
        </p>

        <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
          Let’s build the right water solution for your site.
        </h2>

        <p className="mt-5 max-w-xl leading-8 text-cyan-50">
          Reach out for water ATM projects, treatment plants, pump systems,
          online water monitoring, chlorination units, and wastewater solutions.
        </p>

        <div className="mt-10 space-y-5 text-cyan-50">
          <div className="flex items-start gap-4">
            <Phone className="mt-1 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold text-white">Phone</p>
              <p>+91 8697702088</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <Mail className="mt-1 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold text-white">Email</p>
              <p>info@hydroteclimited.com</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <MapPin className="mt-1 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold text-white">Coverage</p>
              <p>
                India-based projects for communities, institutions, public spaces,
                buildings, and industrial sites
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <form onSubmit={onSubmit} className="grid gap-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your name"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 outline-none transition focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 outline-none transition focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100"
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Organization
            </label>
            <input
              name="company"
              value={form.company}
              onChange={onChange}
              placeholder="Company, institution, apartment, or site"
              className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 outline-none transition focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Requirement <span className="text-red-500">*</span>
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              rows={6}
              placeholder="Tell us what you need: water ATM, pump system, treatment plant, online monitoring, or wastewater solution"
              className="w-full rounded-2xl border border-slate-300 px-4 py-3.5 outline-none transition focus:border-cyan-700 focus:ring-2 focus:ring-cyan-100"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full bg-cyan-700 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-cyan-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Enquiry"
              )}
            </button>

            <div className="min-h-[24px]">
              {submitted ? (
                <p className="flex items-center gap-2 text-sm font-medium text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Thanks! Your message has been received.
                </p>
              ) : error ? (
                <p className="flex items-center gap-2 text-sm font-medium text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </p>
              ) : (
                <p className="text-sm text-slate-500">
                  Your enquiry will be sent directly to our team.
                </p>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}